import { type EnumDestinatarioTipoDestinatarioEnum } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioTipoDestinatarioEnum.ts';
import { type EnumTipoMittenteDTOCodice } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoMittenteDTOCodice.ts';
import { expectNeverAsString } from '@framework/engine/utils/expectNeverAsString.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { getNameInitials } from '@framework/ui/utils/getNameInitials.ts';
import { type TagProps } from 'antd';
import styled from 'styled-components';

const A11yStyledTag = styled(StyledTag)`
  &&& {
    // per contrasto accessibilità A11Y
    // valutare se inserire in UI
    &.ant-tag-green {
      color: #236407;
      background: #f6ffed;
      border-color: #b7eb8f;
    }
    &.ant-tag-orange {
      color: #9b4d03;
      background: #fff7e6;
      border-color: #ffd591;
    }
    &.ant-tag-cyan {
      color: #06767a;
      background: #e6fffb;
      border-color: #87e8de;
    }
  }
`;

/**
 * Ritorna le impostazioni per il tag
 *
 */
function getTagOptions(
  type:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | undefined,
): TagProps {
  if (!type)
    return {
      color: '#cf1322',
      children: t('communication-tag-option.no-type', 'N/D'),
    };
  switch (type) {
    case 'RICHIEDENTE':
      return {
        color: undefined,
        children: t('communication-tag-option.applicant', 'RICHIEDENTE'),
      };
    case 'TITOLARE':
      return {
        color: 'blue',
        children: t('communication-tag-option-holder', 'TITOLARE'),
      };
    // case 'GESTORE':
    //   return {
    //     color: 'blue',
    //     children: t('communication-tag-option.manager', 'GESTORE'),
    //   };
    case 'PROCURATORE':
      return {
        color: 'blue',
        children: t('communication-tag-option.prosecutor', 'PROCURATORE'),
      };
    case 'RESPONSABILE_PROCEDIMENTO':
      return {
        color: 'green',
        children: t(
          'communication-tag-option.responsible-office',
          'UFFICIO RESPONSABILE',
        ),
      };
    case 'ENDOPROCEDIMENTI_PRIMO_LIVELLO':
      return {
        color: 'orange',
        children: t(
          'communication-tag-option.third-organization',
          'ENTE TERZO',
        ),
      };
    case 'DELEGATI_COMPILAZIONE':
      return {
        color: 'cyan',
        children: t(
          'communication-tag-option.delegate-for-comp',
          'DELEGATO ALLA COMPILAZIONE',
        ),
      };
    case 'DELEGATI_FIRMA':
      return {
        color: 'purple',
        children: t(
          'communication-tag-option.delegate-for-sign',
          'DELEGATO ALLA FIRMA',
        ),
      };
    case 'ALTRO_UFFICIO_VARIABILE':
      return {
        color: 'purple', // TODO: da verificare
        children: t(
          'communication-tag-option.other-variable-office',
          'UFFICIO REFERENTE',
        ),
      };
    case 'IMPRESA_TITOLARE':
      return {
        color: 'blue',
        children: t(
          'communication-tag-option.company-holder',
          'IMPRESA TITOLARE',
        ),
      };
    case 'UFFICIO_RICHIEDENTE':
      return {
        color: 'magenta',
        children: t(
          'communication-tag-option.requesting-office',
          'UFFICIO RICHIEDENTE',
        ),
      };
    case 'SOGGETTO_GIURIDICO':
      return {
        color: 'blue',
        children: t(
          'communication-tag-option.legal-subject',
          'SOGGETTO GIURIDICO',
        ),
      };
    case 'UFFICIO_COMPETENZA_SECONDARIA':
      return {
        color: 'volcano',
        children: t(
          'communication-tag-option.secondary-competence-office',
          'UFFICIO COMPETENZA SECONDARIA',
        ),
      };
    case 'SISTEMA':
      return {
        color: 'geekblue',
        children: t('communication-tag-option.system', 'SISTEMA'),
      };
  }
  // Valore non previsto dall'enum a compile-time: mostrato com'è per non
  // rompere il render se il backend introduce nuovi tipi.
  return { children: expectNeverAsString(type) };
}
function getTagOrCodeOptions(
  type:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | undefined,
  generate?: boolean,
) {
  const options = getTagOptions(type);
  return generate
    ? { ...options, children: getNameInitials(options.children as string) }
    : options;
}

export interface ICommunicationUserTypeTagProps {
  type?:
    | EnumTipoMittenteDTOCodice
    | EnumDestinatarioTipoDestinatarioEnum
    | null;
  /** Versione compatta */
  mini?: boolean;
  /** Versione solo Iniziali */
  codeOnly?: boolean;
  /**
   * Testo secondario da accodare all'etichetta del tag (es. la
   * denominazione del Tipo Competenza Secondaria).
   */
  secondaryText?: string;
}

/**
 * Tipo di utente della comunicazione
 */
export function CommunicationUserTypeTag(
  props: ICommunicationUserTypeTagProps,
) {
  const { type, mini, codeOnly, secondaryText } = props;

  if (!type) return null;

  const tagOptions = getTagOrCodeOptions(type, codeOnly);

  if (secondaryText != null) {
    // Per UFFICIO_COMPETENZA_SECONDARIA
    // mostriamo la denominazione del Tipo Competenza Secondaria
    tagOptions.children = `${secondaryText}`;
  }

  return <A11yStyledTag mini={mini} {...tagOptions} />;
}
